import React, { useState } from "react";
import { Loader2, Lock, LockOpen, Pencil, X, Check } from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../ciApi";
import { RagBadge, ConfBadge, TrendBadge, Panel, Kicker, DataTable, ScoreRing } from "../ciWidgets";

const NoteRow = ({ label, text }) =>
  text ? (
    <div className="flex gap-3 text-[13px]">
      <span className="text-white/40 font-semibold shrink-0 w-52">{label}</span>
      <span className="text-white/70 leading-relaxed">{text}</span>
    </div>
  ) : null;

export const DimensionsTab = ({ report, onReload }) => {
  const dims = Object.values(report.dimensions || {});
  const [activeKey, setActiveKey] = useState(dims[0]?.key);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const d = dims.find((x) => x.key === activeKey) || dims[0];
  if (!d) return null;
  const dn = d.directors_note || {};
  const sectionKey = `dimensions.${d.key}`;
  const locked = !!report.locks?.[sectionKey]?.locked;

  const saveNarrative = async () => {
    setBusy(true);
    try {
      await ciApi.patch(`/reports/${report.id}/edit`, { target: "narrative", dimension_key: d.key, value: draft });
      toast.success("Narrative updated");
      setEditing(false);
      onReload && (await onReload());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const toggleLock = async () => {
    try {
      await ciApi.patch(`/reports/${report.id}/locks`, { section: sectionKey, locked: !locked });
      toast.success(locked ? `${d.name} unlocked for editing` : `${d.name} locked & approved`);
      onReload && (await onReload());
    } catch {
      toast.error("Failed to update the lock");
    }
  };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6" data-testid="ci-tab-dimensions">
      <div className="space-y-1.5">
        {dims.map((x) => (
          <button
            key={x.key}
            onClick={() => {
              setActiveKey(x.key);
              setEditing(false);
            }}
            data-testid={`ci-dim-select-${x.key}`}
            className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg text-left text-[13px] transition-colors ${
              x.key === d.key ? "bg-teal-400/10 border border-teal-400/30 text-white" : "border border-transparent text-white/55 hover:bg-white/5"
            }`}
          >
            <span className="leading-snug flex items-center gap-1.5">
              {report.locks?.[`dimensions.${x.key}`]?.locked && <Lock className="w-3 h-3 text-amber-300 shrink-0" />}
              {x.name}
            </span>
            <span className="font-serif tabular-nums text-lg shrink-0">{x.score ?? "—"}</span>
          </button>
        ))}
      </div>

      <div className="space-y-6 min-w-0">
        <Panel className="p-7">
          <div className="flex flex-wrap items-start gap-6">
            <ScoreRing score={d.score} size={120} label={`weight ${d.weight}%`} />
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-serif text-2xl text-white mb-2">{d.name}</h2>
                <button
                  onClick={toggleLock}
                  data-testid="ci-dim-lock-btn"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors shrink-0 ${
                    locked
                      ? "border-amber-400/50 text-amber-300 bg-amber-400/10 hover:bg-amber-400/20"
                      : "border-white/20 text-white/55 hover:text-emerald-300 hover:border-emerald-400/50"
                  }`}
                >
                  {locked ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
                  {locked ? "Locked & approved" : "Lock section"}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <RagBadge rag={d.rag} />
                <TrendBadge trend={d.trend} />
                <ConfBadge level={d.confidence} />
                <span className="text-[11px] text-white/45">Evidence {d.evidence_coverage}% ({d.metrics_scored}/{d.metrics_total} metrics supported)</span>
                {d.peer_percentile != null && <span className="text-[11px] text-white/45">Peer percentile {d.peer_percentile}</span>}
                {d.edited && <span className="text-[10px] uppercase font-bold tracking-[0.12em] text-teal-300">Edited</span>}
              </div>
              <div className="text-[13px] text-white/60 space-y-1">
                <div><b className="text-white/80">Key driver:</b> {d.key_driver}</div>
                <div><b className="text-white/80">Critical issue:</b> {d.critical_issue}</div>
                <div><b className="text-teal-300">Priority action:</b> {d.priority_action}</div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <Kicker>Narrative</Kicker>
              {!editing && (
                <button
                  onClick={() => {
                    if (locked) {
                      toast.error("This section is locked — unlock it to edit");
                      return;
                    }
                    setDraft(d.narrative || "");
                    setEditing(true);
                  }}
                  data-testid="ci-edit-narrative-btn"
                  className={`inline-flex items-center gap-1.5 text-[12px] transition-colors ${locked ? "text-white/25 cursor-not-allowed" : "text-white/50 hover:text-teal-300"}`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit narrative
                </button>
              )}
            </div>
            {editing ? (
              <div>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  data-testid="ci-narrative-textarea"
                  className="w-full min-h-[220px] rounded-lg bg-[#060d18] border border-teal-400/40 px-4 py-3 text-[14px] text-white/85 leading-relaxed focus:outline-none focus:border-teal-300"
                />
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={saveNarrative}
                    disabled={busy || !draft.trim()}
                    data-testid="ci-save-narrative-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 disabled:opacity-40 text-[#06251f] font-bold text-[13px] px-5 py-2 transition-colors"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save narrative
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="inline-flex items-center gap-1.5 text-[13px] text-white/50 hover:text-white transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-[14px] text-white/70 leading-relaxed whitespace-pre-line" data-testid="ci-dim-narrative">{d.narrative}</p>
            )}
          </div>
        </Panel>

        <Panel className="p-7">
          <Kicker className="mb-4">Metrics</Kicker>
          <DataTable
            testId="ci-dim-metrics-table"
            headers={["Metric", "Value", "Period", "Benchmark", "RAG", "Trend", "Confidence", "Type", "Source"]}
            rows={(d.metrics || []).map((m) => [
              <span className={m.applicable === false ? "line-through text-white/30" : "text-white/85"}>
                {m.name}
                {m.applicable === false && <span className="block text-[10px] no-underline text-white/35">Not applicable to this industry / business model</span>}
              </span>,
              <span className="tabular-nums">{m.value != null && m.value !== "" ? `${m.value} ${m.unit || ""}` : <span className="text-white/35 italic">Not available</span>}</span>,
              m.period,
              m.benchmark,
              <RagBadge rag={m.rag} />,
              <TrendBadge trend={m.trend} />,
              <ConfBadge level={m.confidence} />,
              <span className="text-[11px] text-white/45">{(m.statement_type || "").replace(/_/g, " ")}</span>,
              <span className="text-[11px]">
                {m.source}
                {m.source_quality === "attributed" && (
                  <span className="block text-[10px] text-teal-300">AI-attributed — confirm</span>
                )}
              </span>,
            ])}
          />
        </Panel>

        <div className="grid md:grid-cols-2 gap-6">
          <Panel className="p-6">
            <Kicker className="mb-3">Findings</Kicker>
            <ul className="space-y-2.5">
              {(d.findings || []).map((x, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] text-white/70 leading-relaxed">
                  <span className="w-1 h-1 rounded-full bg-teal-300 mt-2 shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </Panel>
          <div className="space-y-6">
            <Panel className="p-6">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-emerald-300 mb-3">Strengths</div>
              <ul className="space-y-2">
                {(d.strengths || []).map((x, i) => (
                  <li key={i} className="text-[13px] text-white/65 leading-relaxed">· {x}</li>
                ))}
              </ul>
            </Panel>
            <Panel className="p-6">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-amber-300 mb-3">Value leaks</div>
              <ul className="space-y-2">
                {(d.value_leaks || []).map((x, i) => (
                  <li key={i} className="text-[13px] text-white/65 leading-relaxed">· {x}</li>
                ))}
              </ul>
            </Panel>
            <Panel className="p-6">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-red-300 mb-3">Risks</div>
              <ul className="space-y-2">
                {(d.risks || []).map((x, i) => (
                  <li key={i} className="text-[13px] text-white/65 leading-relaxed">· {x}</li>
                ))}
              </ul>
            </Panel>
          </div>
        </div>

        <Panel className="p-7 border-amber-400/20">
          <Kicker className="mb-4 text-amber-300">Independent Director's note</Kicker>
          <div className="space-y-3">
            <NoteRow label="Board should understand" text={dn.understand} />
            <NoteRow label="Management should explain" text={dn.explain} />
            <NoteRow label="Should be challenged" text={dn.challenge} />
            <NoteRow label="Evidence still needed" text={dn.evidence_needed} />
            <NoteRow label="Board oversight required" text={dn.oversight} />
          </div>
        </Panel>

        {(d.data_gaps || []).length > 0 && (
          <Panel className="p-6">
            <Kicker className="mb-3">Data gaps — management input required</Kicker>
            <ul className="space-y-2">
              {d.data_gaps.map((x, i) => (
                <li key={i} className="text-[13px] text-white/55 italic">· {x}</li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    </div>
  );
};
